// =============================================================================
// RecruitEdit — /recruits/:id — tabbed profile editor with per-field autosave
// =============================================================================
// Changelog:
//   S25 — Autosave race fix: remove success-handler state re-seed (cross-field
//         clobber bug) + memoize FieldGroup to kill stutter.
//   S28 — Tab deep-link via ?tab= search param (card meeting-step redirect target).
//         Remove meeting modal; inline meeting_at field in Tab 7 log-step form.

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import { can } from '../lib/can.js';
import TabBar from '../components/TabBar.jsx';
// P9b W-10 - the recruit's outreach surface: connect card, assigned RC,
// viewer allowlist, campaigns and the family-digest toggle.
import OutreachTab from '../components/OutreachTab.jsx';
// SQ-S1 - The Call Sheet: the recruit's service-task list (dates, complete,
// un-complete, manual add). Mounted exactly the way OutreachTab is.
import CallSheetTab from '../components/CallSheetTab.jsx';
import PlansTab from '../components/PlansTab.jsx';
import NotificationBell from '../components/NotificationBell.jsx';
import FieldGroup from '../components/FieldGroup.jsx';
import SaveStatusPill from '../components/SaveStatusPill.jsx';
import JsonViewer from '../components/JsonViewer.jsx';
import UrlListEditor from '../components/UrlListEditor.jsx';
// S100 Build 2 - PhotoUploader moved to components/ when MyProfile became its
// second consumer. Endpoint is a prop; this file posts the staff route.
import PhotoUploader from '../components/PhotoUploader.jsx';
import TranscriptUploader from '../components/TranscriptUploader.jsx';
// S100 Build 3.3 - the template gallery. Same component the kid's page uses;
// only the preview endpoint and the copy differ.
import TemplateGallery from '../components/TemplateGallery.jsx';

// =============================================================================
// Constants
// =============================================================================

// S27 — synced to 9-value enum (was 11 stale values; RecruitList was updated in S26, this file was missed)
const PIPELINE_STATUSES = [
  { value: 'new_lead',           label: 'New Lead' },
  { value: 'pre_enrollment',     label: 'Pre-Enrollment' },
  { value: 'enrolled_active',    label: 'Enrolled / Active' },
  { value: 'enrolled_paused',    label: 'Enrolled / Paused' },
  { value: 'enrolled_completed', label: 'Enrolled / Completed' },
  { value: 'dormant_lead',       label: 'Dormant Lead' },
  { value: 'dead_lead',          label: 'Dead Lead' },
  { value: 'intake_error',       label: 'Intake Error' },
  { value: 'test_account',       label: 'Test Account' },
];

const STATUS_COLORS = {
  new_lead:           'bg-gray-200 text-silver-text',
  pre_enrollment:     'bg-amber-100 text-amber-800',
  enrolled_active:    'bg-green-100 text-green-800',
  enrolled_paused:    'bg-blue-100 text-blue-800',
  enrolled_completed: 'bg-hunter-deep text-white',
  dormant_lead:       'bg-slate-200 text-slate-600',
  dead_lead:          'bg-red-200 text-red-800',
  intake_error:       'bg-orange-200 text-orange-800',
  test_account:       'bg-purple-100 text-purple-700',
};

// Disposition step labels + meeting-step set (kept identical to RecruitList.jsx)
const STEP_LABELS = {
  intro_text:                     'Intro Text Sent',
  player_call_1:                  'Player Call 1',
  player_call_2:                  'Player Call 2',
  player_call_3:                  'Player Call 3',
  parent_call_1:                  'Parent Call 1',
  parent_call_2:                  'Parent Call 2',
  parent_call_3:                  'Parent Call 3',
  enrollment_meeting_scheduled:   'Enrollment Meeting Scheduled',
  enrollment_meeting_rescheduled: 'Enrollment Meeting Rescheduled',
  enrollment_meeting_pending:     'Enrollment Meeting Pending',
  enrollment_meeting_noshow_1:    'Enrollment Meeting No-Show 1',
  enrollment_meeting_noshow_2:    'Enrollment Meeting No-Show 2',
  enrollment_meeting_noshow_3:    'Enrollment Meeting No-Show 3',
};

const MEETING_STEPS = new Set([
  'enrollment_meeting_scheduled',
  'enrollment_meeting_rescheduled',
]);

const DISPO_STEPS = Object.keys(STEP_LABELS);

const INTAKE_PARTICIPANT_OPTIONS = [
  { value: 'recruit', label: 'Recruit' },
  { value: 'parent',  label: 'Parent' },
];

const COMMITMENT_OPTIONS = [1, 2, 3, 4, 5].map(n => ({ value: String(n), label: String(n) }));

const TABS = [
  { id: 'contact',    label: 'Contact' },
  { id: 'academic',   label: 'Academic' },
  { id: 'athletic',   label: 'Athletic' },
  { id: 'recruiting', label: 'Recruiting Activity' },
  { id: 'nil',        label: 'NIL & Branding' },
  { id: 'goals',      label: 'Goals' },
  { id: 'intake',     label: 'Intake & Notes' },
  { id: 'payments',   label: 'Payments' },
  { id: 'outreach',   label: 'Outreach' },
  { id: 'callsheet',  label: 'Call Sheet' },
  { id: 'plans',      label: 'Plans' },
];

// Fields that belong to player_profiles (vs recruits)
const PROFILE_TABLE_FIELDS = new Set([
  'jersey_number',
  'forty_time', 'forty_time_is_laser',
  'ten_yard_split', 'twenty_yard_shuttle', 'three_cone',
  'vertical_jump_inches', 'broad_jump_inches',
  'bench_press_lbs', 'bench_press_reps', 'squat_lbs', 'power_clean_lbs', 'deadlift_lbs',
  'intended_major',
  'profile_photo_url', 'bio', 'hudl_url', 'highlight_video_urls',
  'twitter_handle', 'instagram_handle', 'tiktok_handle',
]);

// Checkbox fields — onChange fires actual booleans; coerce must pass through
const BOOLEAN_FIELDS = new Set(['parent_aware', 'forty_time_is_laser']);

// Fields that need numeric coercion
const NUMBER_FIELDS = new Set([
  'height_inches', 'weight_lbs', 'graduation_year', 'gpa',
  'sat_score', 'act_score', 'social_followers_total',
  'recruit_commitment_level', 'parent_commitment_level',
  'jersey_number',
  'forty_time', 'ten_yard_split', 'twenty_yard_shuttle', 'three_cone',
  'vertical_jump_inches', 'broad_jump_inches',
  'bench_press_lbs', 'bench_press_reps', 'squat_lbs', 'power_clean_lbs', 'deadlift_lbs',
]);

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5)    return 'just now';
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return formatDate(iso);
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function coerce(field, value) {
  if (BOOLEAN_FIELDS.has(field)) {
    if (typeof value === 'boolean') return value;
    if (value === 'true')  return true;
    if (value === 'false') return false;
    return null;
  }
  if (NUMBER_FIELDS.has(field)) {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return isNaN(n) ? null : n;
  }
  return value === '' ? null : value;
}

function displayValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return String(value);
  return value;
}

// =============================================================================
// Main component
// =============================================================================

export default function RecruitEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [me, setMe] = useState(null);
  const [recruit, setRecruit] = useState(null);
  const [profile, setProfile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  // S28: initialize from ?tab= search param so card meeting-step redirects land correctly
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return TABS.some(t => t.id === tab) ? tab : 'contact';
  });
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Disposition panel state
  const [dispos, setDispos] = useState([]);
  const [disposLoading, setDisposLoading] = useState(false);
  const [disposError, setDisposError] = useState(null);
  const [logStep, setLogStep] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logStepDate, setLogStepDate] = useState(() => new Date().toISOString().split('T')[0]);
  // meetingAt: now drives the inline datetime-local field (S28: modal removed)
  const [meetingAt, setMeetingAt] = useState('');

  // Admin notes state
  const [noteInput, setNoteInput] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  // Per-field debounce timers and in-flight abort controllers
  const timers = useRef({});
  const abortControllers = useRef({});
  const lastRetryFn = useRef(null);

  useEffect(() => {
    return () => { Object.values(timers.current).forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [meData, recruitData, teamData, disposData] = await Promise.all([
          api.get('/api/auth/me'),
          api.get(`/api/recruits/${id}`),
          api.get('/api/recruits/team-members'),
          api.get(`/api/recruits/${id}/dispositions`),
        ]);
        setMe(meData);
        setRecruit(recruitData.recruit);
        setProfile(recruitData.profile || {});
        // S100 Build 3.1 — public-page look options, from the server registry.
        setTemplates(recruitData.templates || []);
        setTeamMembers(teamData.team_members || []);
        setDispos(disposData.dispositions || []);
      } catch (err) {
        setPageError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  // ---------------------------------------------------------------------------
  // Autosave — AbortController aborts superseded in-flight saves per field
  // ---------------------------------------------------------------------------

  const save = useCallback(async (table, field, rawValue) => {
    // Abort any prior in-flight save for this field
    abortControllers.current[field]?.abort();
    const controller = new AbortController();
    abortControllers.current[field] = controller;

    const value = field === 'highlight_video_urls'
      ? (Array.isArray(rawValue) ? rawValue.filter(u => u.trim().length > 0) : [])
      : coerce(field, rawValue);

    const body = { [field]: value };
    const endpoint = table === 'profile'
      ? `/api/recruits/${id}/profile`
      : `/api/recruits/${id}`;

    setSaveStatus('saving');
    setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

    const fn = async () => {
      try {
        const result = await api(endpoint, {
          method: 'PATCH',
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        abortControllers.current[field] = null;
        // Server has the value; do NOT re-seed local state from the response.
        // Re-seeding here clobbers in-flight keystrokes in other fields
        // (cross-field race). Local state is authoritative until unmount or
        // route change (the load useEffect handles those cases).
        setLastSavedAt(new Date());
        setSaveStatus('saved');
        lastRetryFn.current = null;
      } catch (err) {
        if (err.name === 'AbortError') return; // superseded — new save is in flight
        setFieldErrors(prev => ({ ...prev, [field]: err.message }));
        setSaveStatus('error');
        lastRetryFn.current = fn;
      }
    };

    await fn();
  }, [id]);

  function scheduleField(table, field, value) {
    clearTimeout(timers.current[field]);
    timers.current[field] = setTimeout(() => save(table, field, value), 600);
  }

  function updateRecruit(field, value) {
    setRecruit(prev => ({ ...prev, [field]: value }));
    scheduleField('recruit', field, value);
  }

  function updateProfile(field, value) {
    setProfile(prev => ({ ...prev, [field]: value }));
    scheduleField('profile', field, value);
  }

  function handleRetry() {
    if (lastRetryFn.current) lastRetryFn.current();
  }

  // ---------------------------------------------------------------------------
  // Disposition + notes actions
  // ---------------------------------------------------------------------------

  const fetchDispos = useCallback(async () => {
    setDisposLoading(true);
    setDisposError(null);
    try {
      const data = await api.get(`/api/recruits/${id}/dispositions`);
      setDispos(data.dispositions || []);
    } catch (err) {
      setDisposError(err.message);
    } finally {
      setDisposLoading(false);
    }
  }, [id]);

  // S28: no modal — meeting steps submit directly with the inline meetingAt field
  async function handleLogStep() {
    if (!logStep) return;
    await submitDispoLog();
  }

  async function submitDispoLog() {
    try {
      const body = {
        step_date: logStepDate || null,
        note: logNote || null,
      };
      // Include meeting_at when a meeting step is selected and the field is filled
      if (MEETING_STEPS.has(logStep) && meetingAt) {
        body.meeting_at = meetingAt ? new Date(meetingAt).toISOString() : null;
      }
      await api.put(`/api/recruits/${id}/dispositions/${logStep}`, body);
      setLogStep('');
      setLogNote('');
      setLogStepDate(new Date().toISOString().split('T')[0]);
      setMeetingAt('');
      await fetchDispos();
    } catch (err) {
      console.error('[dispo log] failed:', err.message);
    }
  }

  async function handleAddNote() {
    if (!noteInput.trim()) return;
    setNoteSubmitting(true);
    try {
      const result = await api.post(`/api/recruits/${id}/notes`, {
        note: noteInput.trim(),
        author: me?.team?.full_name || undefined,
      });
      setRecruit(prev => ({ ...prev, admin_notes: result.recruit.admin_notes }));
      setNoteInput('');
    } catch (err) {
      console.error('[add note] failed:', err.message);
    } finally {
      setNoteSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  function rf(field)  { return displayValue(recruit?.[field]); }
  function pf(field)  { return displayValue(profile?.[field]); }
  function rfe(field) { return fieldErrors[field]; }

  const teamMemberOptions = teamMembers.map(m => ({
    value: m.id,
    label: `${m.full_name} (${m.role})`,
  }));

  // Derived feet/inches from height_inches smallint
  const heightFeet   = recruit?.height_inches != null ? Math.floor(recruit.height_inches / 12) : null;
  const heightInches = recruit?.height_inches != null ? recruit.height_inches % 12 : null;

  function updateHeight(feet, inches) {
    const val = (feet === null && inches === null)
      ? null
      : (feet ?? 0) * 12 + (inches ?? 0);
    updateRecruit('height_inches', val);
  }

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-silver-a flex items-center justify-center">
        <p className="text-silver-mute text-sm">Loading…</p>
      </div>
    );
  }

  if (pageError || !recruit) {
    return (
      <div className="min-h-screen bg-silver-a flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-sm mb-4">{pageError || 'Recruit not found.'}</p>
          <Link to="/recruits" className="btn-primary text-sm">Back to list</Link>
        </div>
      </div>
    );
  }

  const status = recruit.status;

  return (
    <div className="min-h-screen bg-silver-a">

      {/* Header */}
      <header className="bg-hunter-deep border-b border-hunter">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-white text-2xl leading-none">CCF Front Office</div>
            <div className="text-mid-green text-[0.65rem] tracking-[0.25em] uppercase font-bold mt-1">
              Coach Cole Football
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <NotificationBell />
            {me && (
              <span className="text-silver-a">
                Welcome, <span className="font-bold text-white">{me.team.full_name}</span>
              </span>
            )}
            <Link to="/" className="text-silver-a hover:text-white underline">← Front Office</Link>
            <button onClick={signOut} className="text-silver-a hover:text-white underline">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Back link */}
        <Link to="/recruits" className="text-sm text-mid-green hover:underline">← All Recruits</Link>

        {/* Title row */}
        <div className="mt-3 mb-1 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-2xl text-silver-text">
              {recruit.first_name} {recruit.last_name}
            </h1>
            {status && (
              <span className={`text-[0.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_COLORS[status] || 'bg-gray-200 text-silver-text'}`}>
                {status.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* S100 Build 3 — public page. Publish mints the frozen slug
                server-side; unpublish clears published_at only. */}
            <PublishProfileButton
              recruitId={id}
              slug={profile?.slug}
              publishedAt={profile?.published_at}
              onChange={(patch) => setProfile(prev => ({ ...prev, ...patch }))}
            />
            {/* S100 Build 2 — player login. Linked state reads off
                profile.user_id; the server owns every other rule. */}
            <InvitePlayerButton recruitId={id} linked={!!profile?.user_id} />
            <SaveStatusPill
              status={saveStatus}
              lastSavedAt={lastSavedAt}
              onRetry={handleRetry}
            />
          </div>
        </div>

        {/* Metadata strip */}
        <p className="text-xs text-silver-mute mb-6">
          Created {formatDate(recruit.created_at)}
          {recruit.updated_at && ` • Updated ${relativeTime(recruit.updated_at)}`}
        </p>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 px-6 pt-4">
            <TabBar tabs={TABS} activeTab={activeTab} onSelect={setActiveTab} />
          </div>

          <div className="p-6">

            {/* ----------------------------------------------------------------
                Tab 1 — Contact
            ---------------------------------------------------------------- */}
            {activeTab === 'contact' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldGroup label="First Name" name="first_name" value={rf('first_name')} onChange={v => updateRecruit('first_name', v)} error={rfe('first_name')} />
                  <FieldGroup label="Last Name"  name="last_name"  value={rf('last_name')}  onChange={v => updateRecruit('last_name', v)}  error={rfe('last_name')} />
                  <FieldGroup label="Email"      name="email"      value={rf('email')}      onChange={v => updateRecruit('email', v)}      type="email" error={rfe('email')} />
                  <FieldGroup label="Phone"      name="phone"      value={rf('phone')}      onChange={v => updateRecruit('phone', v)}      type="tel"   error={rfe('phone')} />
                  <FieldGroup label="State"      name="state"      value={rf('state')}      onChange={v => updateRecruit('state', v)}      error={rfe('state')} />
                  <div />

                  {/* Parent / Guardian divider */}
                  <div className="col-span-full flex items-center gap-3 py-1">
                    <div className="flex-1 h-px bg-hunter opacity-40" />
                    <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-hunter">Parent / Guardian</span>
                    <div className="flex-1 h-px bg-hunter opacity-40" />
                  </div>

                  <FieldGroup label="Parent First Name" name="parent_first_name" value={rf('parent_first_name')} onChange={v => updateRecruit('parent_first_name', v)} error={rfe('parent_first_name')} />
                  <FieldGroup label="Parent Last Name"  name="parent_last_name"  value={rf('parent_last_name')}  onChange={v => updateRecruit('parent_last_name', v)}  error={rfe('parent_last_name')} />
                  <FieldGroup label="Parent Email"      name="parent_email"      value={rf('parent_email')}      onChange={v => updateRecruit('parent_email', v)}      type="email" error={rfe('parent_email')} />
                  <FieldGroup label="Parent Phone"      name="parent_phone"      value={rf('parent_phone')}      onChange={v => updateRecruit('parent_phone', v)}      type="tel"   error={rfe('parent_phone')} />
                </div>
                <FieldGroup
                  label="Current Recruiting Advisors"
                  name="current_recruiting_advisors"
                  value={rf('current_recruiting_advisors')}
                  onChange={v => updateRecruit('current_recruiting_advisors', v)}
                  type="textarea"
                  rows={3}
                  error={rfe('current_recruiting_advisors')}
                />
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 2 — Academic
            ---------------------------------------------------------------- */}
            {activeTab === 'academic' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Graduation Year" name="graduation_year" value={rf('graduation_year')} onChange={v => updateRecruit('graduation_year', v)} type="number" error={rfe('graduation_year')} />
                <FieldGroup label="GPA"             name="gpa"             value={rf('gpa')}             onChange={v => updateRecruit('gpa', v)}             type="number" error={rfe('gpa')} />
                <FieldGroup label="SAT Score"       name="sat_score"       value={rf('sat_score')}       onChange={v => updateRecruit('sat_score', v)}       type="number" error={rfe('sat_score')} />
                <FieldGroup label="ACT Score"       name="act_score"       value={rf('act_score')}       onChange={v => updateRecruit('act_score', v)}       type="number" error={rfe('act_score')} />
                <FieldGroup label="Next Test Date"  name="next_test_date"  value={rf('next_test_date')}  onChange={v => updateRecruit('next_test_date', v)}  type="date"   error={rfe('next_test_date')} />
                <FieldGroup label="NCAA ID"         name="ncaa_id"         value={rf('ncaa_id')}         onChange={v => updateRecruit('ncaa_id', v)}         error={rfe('ncaa_id')} />
                <FieldGroup label="Current School"  name="current_school"  value={rf('current_school')}  onChange={v => updateRecruit('current_school', v)}  error={rfe('current_school')} />
                <FieldGroup label="School City"     name="school_city"     value={rf('school_city')}     onChange={v => updateRecruit('school_city', v)}     error={rfe('school_city')} />
                <FieldGroup label="School Website"  name="school_website"  value={rf('school_website')}  onChange={v => updateRecruit('school_website', v)}  type="url"    error={rfe('school_website')} />
                <FieldGroup label="HS Division"     name="hs_division"     value={rf('hs_division')}     onChange={v => updateRecruit('hs_division', v)}     error={rfe('hs_division')} />
                <FieldGroup label="Intended Major"  name="intended_major"  value={pf('intended_major')}  onChange={v => updateProfile('intended_major', v)}  error={rfe('intended_major')} />
                <div className="md:col-span-2">
                  <TranscriptUploader
                    endpoint={`/api/recruits/${id}/transcript`}
                    transcript={profile?.metadata?.transcript}
                    onUploaded={(transcript) => {
                      setProfile(prev => ({
                        ...prev,
                        metadata: { ...(prev?.metadata || {}), transcript },
                      }));
                    }}
                  />
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 3 — Athletic
            ---------------------------------------------------------------- */}
            {activeTab === 'athletic' && (
              <div className="space-y-8">
                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-4">Recruit</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FieldGroup label="Primary Position" name="primary_position" value={rf('primary_position')} onChange={v => updateRecruit('primary_position', v)} error={rfe('primary_position')} />

                    {/* Height — feet/inches double dropdown, stored as single smallint */}
                    <div>
                      <label className="label-dark">Height</label>
                      <div className="flex gap-2 mt-1">
                        <select
                          value={heightFeet ?? ''}
                          onChange={e => {
                            const f = e.target.value === '' ? null : Number(e.target.value);
                            updateHeight(f, heightInches);
                          }}
                          className="input flex-1"
                        >
                          <option value="">—</option>
                          {[4, 5, 6, 7].map(f => (
                            <option key={f} value={f}>{f} ft</option>
                          ))}
                        </select>
                        <select
                          value={heightInches ?? ''}
                          onChange={e => {
                            const i = e.target.value === '' ? null : Number(e.target.value);
                            updateHeight(heightFeet, i);
                          }}
                          className="input flex-1"
                        >
                          <option value="">—</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>{i} in</option>
                          ))}
                        </select>
                      </div>
                      {rfe('height_inches') && <p className="text-xs text-red-600 mt-1">{rfe('height_inches')}</p>}
                    </div>

                    <FieldGroup label="Weight (lbs)"   name="weight_lbs"      value={rf('weight_lbs')}      onChange={v => updateRecruit('weight_lbs', v)}      type="number" error={rfe('weight_lbs')} />
                    <FieldGroup label="Offense Scheme" name="offense_scheme"  value={rf('offense_scheme')}  onChange={v => updateRecruit('offense_scheme', v)}  error={rfe('offense_scheme')} />
                    <FieldGroup label="Defense Scheme" name="defense_scheme"  value={rf('defense_scheme')}  onChange={v => updateRecruit('defense_scheme', v)}  error={rfe('defense_scheme')} />
                    <FieldGroup label="Special Teams"  name="special_teams"   value={rf('special_teams')}   onChange={v => updateRecruit('special_teams', v)}   error={rfe('special_teams')} />
                    <FieldGroup label="Other Sports"   name="other_sports"    value={rf('other_sports')}    onChange={v => updateRecruit('other_sports', v)}    error={rfe('other_sports')} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <FieldGroup label="HS Coaches Info" name="hs_coaches_info" value={rf('hs_coaches_info')} onChange={v => updateRecruit('hs_coaches_info', v)} type="textarea" rows={3} error={rfe('hs_coaches_info')} />
                    <FieldGroup label="Best Competition Recruit has Played Against" name="best_competition" value={rf('best_competition')} onChange={v => updateRecruit('best_competition', v)} type="textarea" rows={2} error={rfe('best_competition')} />
                  </div>
                </div>

                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-4">Player Profile</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FieldGroup label="Secondary Position" name="secondary_position"  value={rf('secondary_position')}  onChange={v => updateRecruit('secondary_position', v)}  error={rfe('secondary_position')} />
                    <FieldGroup label="Jersey Number"      name="jersey_number"       value={pf('jersey_number')}       onChange={v => updateProfile('jersey_number', v)}       type="number" error={rfe('jersey_number')} />
                    <FieldGroup label="40-Yard Dash"       name="forty_time"          value={pf('forty_time')}          onChange={v => updateProfile('forty_time', v)}          type="number" error={rfe('forty_time')} />
                    <FieldGroup
                      label="Laser Timed"
                      name="forty_time_is_laser"
                      value={!!profile?.forty_time_is_laser}
                      onChange={v => updateProfile('forty_time_is_laser', v)}
                      type="checkbox"
                      error={rfe('forty_time_is_laser')}
                    />
                    <FieldGroup label="10-Yard Split"      name="ten_yard_split"      value={pf('ten_yard_split')}      onChange={v => updateProfile('ten_yard_split', v)}      type="number" error={rfe('ten_yard_split')} />
                    <FieldGroup label="Shuttle (5-10-5)"   name="twenty_yard_shuttle"  value={pf('twenty_yard_shuttle')} onChange={v => updateProfile('twenty_yard_shuttle', v)} type="number" error={rfe('twenty_yard_shuttle')} />
                    <FieldGroup label="3-Cone Drill"       name="three_cone"          value={pf('three_cone')}          onChange={v => updateProfile('three_cone', v)}          type="number" error={rfe('three_cone')} />
                    <FieldGroup label="Vertical Jump (in)" name="vertical_jump_inches" value={pf('vertical_jump_inches')} onChange={v => updateProfile('vertical_jump_inches', v)} type="number" error={rfe('vertical_jump_inches')} />
                    <FieldGroup label="Broad Jump (in)"    name="broad_jump_inches"   value={pf('broad_jump_inches')}   onChange={v => updateProfile('broad_jump_inches', v)}   type="number" error={rfe('broad_jump_inches')} />
                    <FieldGroup label="Bench Press (lbs)"  name="bench_press_lbs"     value={pf('bench_press_lbs')}     onChange={v => updateProfile('bench_press_lbs', v)}     type="number" error={rfe('bench_press_lbs')} />
                    <FieldGroup label="Bench Press (reps)" name="bench_press_reps"    value={pf('bench_press_reps')}    onChange={v => updateProfile('bench_press_reps', v)}    type="number" error={rfe('bench_press_reps')} />
                    <FieldGroup label="Squat (lbs)"        name="squat_lbs"           value={pf('squat_lbs')}           onChange={v => updateProfile('squat_lbs', v)}           type="number" error={rfe('squat_lbs')} />
                    <FieldGroup label="Power Clean (lbs)"  name="power_clean_lbs"     value={pf('power_clean_lbs')}     onChange={v => updateProfile('power_clean_lbs', v)}     type="number" error={rfe('power_clean_lbs')} />
                    <FieldGroup label="Deadlift (lbs)"     name="deadlift_lbs"        value={pf('deadlift_lbs')}        onChange={v => updateProfile('deadlift_lbs', v)}        type="number" error={rfe('deadlift_lbs')} />
                  </div>
                </div>
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 4 — Recruiting Activity
            ---------------------------------------------------------------- */}
            {activeTab === 'recruiting' && (
              <div className="space-y-4">
                <FieldGroup label="Current Offers"            name="current_offers"            value={rf('current_offers')}            onChange={v => updateRecruit('current_offers', v)}            type="textarea" rows={2} error={rfe('current_offers')} />
                <FieldGroup label="Current Interest"          name="current_interest"          value={rf('current_interest')}          onChange={v => updateRecruit('current_interest', v)}          type="textarea" rows={2} error={rfe('current_interest')} />
                <FieldGroup label="Camps & Combines"          name="camps_combines"            value={rf('camps_combines')}            onChange={v => updateRecruit('camps_combines', v)}            type="textarea" rows={2} error={rfe('camps_combines')} />
                <FieldGroup label="College Visits"            name="college_visits"            value={rf('college_visits')}            onChange={v => updateRecruit('college_visits', v)}            type="textarea" rows={2} error={rfe('college_visits')} />
                <FieldGroup label="College Coaches Contacted" name="college_coaches_contacted" value={rf('college_coaches_contacted')} onChange={v => updateRecruit('college_coaches_contacted', v)} type="textarea" rows={2} error={rfe('college_coaches_contacted')} />
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 5 — NIL & Branding
            ---------------------------------------------------------------- */}
            {activeTab === 'nil' && (
              <div className="space-y-4">
                {/* S100 Build 1 — staff photo upload. On success the SERVER has
                    already written the pointer; setProfile only mirrors it into
                    local state. updateProfile would schedule an autosave and
                    double-write the same value — deliberately not used here. */}
                {/* S100 Build 3.3 — the public page's look, chosen from live
                    mini-renders. Lives here rather than the title row: eleven
                    thumbnails do not belong beside Publish, and this tab is
                    already where the public-facing presentation is edited. */}
                <TemplateGallery
                  templates={templates}
                  value={profile?.metadata?.template || templates[0]?.key}
                  previewUrl={`/api/recruits/${id}/profile/preview`}
                  heading="Public page look"
                  blurb="How this player's public profile page renders. Select one and apply — the player can change it too."
                  onApply={async (key) => {
                    const body = await api.patch(`/api/recruits/${id}/profile/template`, { template: key });
                    setProfile(prev => ({ ...prev, metadata: { ...(prev?.metadata || {}), template: body.template } }));
                  }}
                />
                <PhotoUploader
                  endpoint={`/api/recruits/${id}/photo`}
                  url={pf('profile_photo_url')}
                  onUploaded={url => setProfile(prev => ({ ...prev, profile_photo_url: url }))}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldGroup label="Social Followers (total)" name="social_followers_total" value={rf('social_followers_total')} onChange={v => updateRecruit('social_followers_total', v)} type="number" error={rfe('social_followers_total')} />
                  <FieldGroup label="Profile Photo URL"        name="profile_photo_url"      value={pf('profile_photo_url')}      onChange={v => updateProfile('profile_photo_url', v)}      type="url"    error={rfe('profile_photo_url')} helpText="Set automatically by the uploader above — or paste an external link." />
                  <FieldGroup label="Hudl URL"                 name="hudl_url"               value={pf('hudl_url')}               onChange={v => updateProfile('hudl_url', v)}               type="url"    error={rfe('hudl_url')} />
                  <FieldGroup label="Twitter/X Handle"         name="twitter_handle"         value={pf('twitter_handle')}         onChange={v => updateProfile('twitter_handle', v)}         error={rfe('twitter_handle')} />
                  <FieldGroup label="Instagram Handle"         name="instagram_handle"        value={pf('instagram_handle')}       onChange={v => updateProfile('instagram_handle', v)}       error={rfe('instagram_handle')} />
                  <FieldGroup label="TikTok Handle"            name="tiktok_handle"           value={pf('tiktok_handle')}          onChange={v => updateProfile('tiktok_handle', v)}          error={rfe('tiktok_handle')} />
                </div>
                <FieldGroup label="NIL Experience"  name="nil_experience"  value={rf('nil_experience')}  onChange={v => updateRecruit('nil_experience', v)}  type="textarea" rows={2} error={rfe('nil_experience')} />
                <FieldGroup label="Content Recruit Currently Posts (Football? Training? Lifestyle?)" name="content_themes" value={rf('content_themes')} onChange={v => updateRecruit('content_themes', v)} type="textarea" rows={2} error={rfe('content_themes')} />
                <FieldGroup label="Unique Story"    name="unique_story"    value={rf('unique_story')}    onChange={v => updateRecruit('unique_story', v)}    type="textarea" rows={3} error={rfe('unique_story')} />
                <FieldGroup label="Bio"             name="bio"             value={pf('bio')}             onChange={v => updateProfile('bio', v)}             type="textarea" rows={2} error={rfe('bio')} />
                <UrlListEditor
                  label="Highlight Video URLs"
                  value={Array.isArray(profile?.highlight_video_urls) ? profile.highlight_video_urls : []}
                  onChange={v => updateProfile('highlight_video_urls', v)}
                />
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 6 — Goals
            ---------------------------------------------------------------- */}
            {activeTab === 'goals' && (
              <div className="space-y-4">
                <FieldGroup label="Dream Level"           name="dream_level"          value={rf('dream_level')}          onChange={v => updateRecruit('dream_level', v)}          error={rfe('dream_level')} />
                <FieldGroup label="Target Schools"        name="target_schools"       value={rf('target_schools')}       onChange={v => updateRecruit('target_schools', v)}       type="textarea" rows={2} error={rfe('target_schools')} />
                <FieldGroup label="Biggest Hurdle"        name="biggest_hurdle"       value={rf('biggest_hurdle')}       onChange={v => updateRecruit('biggest_hurdle', v)}       type="textarea" rows={3} error={rfe('biggest_hurdle')} />
                <FieldGroup label="Recruiting Help Wanted" name="recruiting_help_wants" value={rf('recruiting_help_wants')} onChange={v => updateRecruit('recruiting_help_wants', v)} type="textarea" rows={3} error={rfe('recruiting_help_wants')} />
              </div>
            )}

            {/* ----------------------------------------------------------------
                Tab 7 — Intake & Notes
                S27 — dispo panel, intake highlights panel, admin-notes thread
                S28 — meeting modal removed; inline meeting_at field added
            ---------------------------------------------------------------- */}
            {activeTab === 'intake' && (
              <div className="space-y-6">

                {/* Pipeline status + intake metadata */}
                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-4">Pipeline</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldGroup
                      label="Pipeline Status"
                      name="status"
                      value={rf('status')}
                      onChange={v => updateRecruit('status', v)}
                      type="select"
                      options={PIPELINE_STATUSES}
                      error={rfe('status')}
                    />
                    <FieldGroup
                      label="Intake Participant"
                      name="intake_participant"
                      value={rf('intake_participant')}
                      onChange={v => updateRecruit('intake_participant', v)}
                      type="select"
                      options={INTAKE_PARTICIPANT_OPTIONS}
                      error={rfe('intake_participant')}
                    />
                    {recruit?.intake_participant === 'recruit' && (
                      <FieldGroup
                        label="Parent Aware"
                        name="parent_aware"
                        value={!!recruit?.parent_aware}
                        onChange={v => updateRecruit('parent_aware', v)}
                        type="checkbox"
                        error={rfe('parent_aware')}
                      />
                    )}
                    <FieldGroup
                      label="Recruit Commitment Level (1–5)"
                      name="recruit_commitment_level"
                      value={rf('recruit_commitment_level')}
                      onChange={v => updateRecruit('recruit_commitment_level', v)}
                      type="select"
                      options={COMMITMENT_OPTIONS}
                      error={rfe('recruit_commitment_level')}
                    />
                    <FieldGroup
                      label="Parent Commitment Level (1–5)"
                      name="parent_commitment_level"
                      value={rf('parent_commitment_level')}
                      onChange={v => updateRecruit('parent_commitment_level', v)}
                      type="select"
                      options={COMMITMENT_OPTIONS}
                      error={rfe('parent_commitment_level')}
                    />
                    <FieldGroup
                      label="Referred By"
                      name="referred_by_team_id"
                      value={rf('referred_by_team_id')}
                      onChange={v => updateRecruit('referred_by_team_id', v)}
                      type="select"
                      options={teamMemberOptions}
                      error={rfe('referred_by_team_id')}
                    />
                    <FieldGroup
                      label="Referral Source (if not a CCF Team Member)"
                      name="referral_source"
                      value={rf('referral_source')}
                      onChange={v => updateRecruit('referral_source', v)}
                      error={rfe('referral_source')}
                    />
                    <FieldGroup
                      label="Source"
                      name="source"
                      value={rf('source')}
                      onChange={v => updateRecruit('source', v)}
                      error={rfe('source')}
                    />
                  </div>
                  <div className="mt-4">
                    <FieldGroup label="Final Notes" name="final_notes" value={rf('final_notes')} onChange={v => updateRecruit('final_notes', v)} type="textarea" rows={3} error={rfe('final_notes')} />
                  </div>
                </div>

                {/* Disposition tracker */}
                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">Disposition</h3>

                  {/* Upcoming meeting callout */}
                  {dispos[0] && MEETING_STEPS.has(dispos[0].step) && dispos[0].meeting_at && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm">
                      <span className="font-bold text-amber-800">Upcoming: </span>
                      <span className="text-amber-700">{STEP_LABELS[dispos[0].step]}</span>
                      <span className="text-amber-600 ml-2">{formatDateTime(dispos[0].meeting_at)}</span>
                    </div>
                  )}

                  {/* Logged steps */}
                  {disposLoading && <p className="text-sm text-silver-mute">Loading…</p>}
                  {disposError && <p className="text-sm text-red-600">{disposError}</p>}
                  {!disposLoading && dispos.length === 0 && (
                    <p className="text-sm text-silver-mute italic mb-3">No steps logged yet.</p>
                  )}
                  {dispos.length > 0 && (
                    <div className="border border-gray-200 rounded-md divide-y divide-gray-100 mb-4">
                      {dispos.map(d => (
                        <div key={d.step} className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-silver-text">{STEP_LABELS[d.step] || d.step}</span>
                            {d.step_date && <span className="text-silver-mute text-xs">{formatDate(d.step_date)}</span>}
                            {d.left_vm    && <span className="text-xs bg-gray-100 text-silver-mute px-1.5 py-0.5 rounded">VM</span>}
                            {d.sent_text  && <span className="text-xs bg-gray-100 text-silver-mute px-1.5 py-0.5 rounded">Text</span>}
                            {d.sent_email && <span className="text-xs bg-gray-100 text-silver-mute px-1.5 py-0.5 rounded">Email</span>}
                          </div>
                          {MEETING_STEPS.has(d.step) && d.meeting_at && (
                            <p className="text-xs text-amber-700 mt-0.5">{formatDateTime(d.meeting_at)}</p>
                          )}
                          {d.note && <p className="text-silver-text mt-1 text-xs">{d.note}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Log step form — S28: inline meeting_at replaces modal */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <select value={logStep} onChange={e => setLogStep(e.target.value)} className="input flex-1 text-sm">
                        <option value="">Select step…</option>
                        {DISPO_STEPS.map(s => (
                          <option key={s} value={s}>{STEP_LABELS[s]}</option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={logStepDate}
                        onChange={e => setLogStepDate(e.target.value)}
                        className="input w-36 text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Note (optional)"
                      value={logNote}
                      onChange={e => setLogNote(e.target.value)}
                      className="input text-sm"
                    />
                    {/* Inline meeting date/time — shown only when a meeting step is selected */}
                    {MEETING_STEPS.has(logStep) && (
                      <div>
                        <label className="label-dark text-sm">Meeting Date &amp; Time</label>
                        <input
                          type="datetime-local"
                          value={meetingAt}
                          onChange={e => setMeetingAt(e.target.value)}
                          className="input w-full text-sm mt-1"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleLogStep}
                      disabled={!logStep}
                      className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Log Step
                    </button>
                  </div>
                </div>

                {/* Intake highlights — custom panel replacing JsonViewer */}
                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">Intake Highlights</h3>
                  {(() => {
                    const h = recruit?.intake_highlights;
                    const hasNote = h && h.completion_note;
                    const hasNotable = h && Array.isArray(h.notable) && h.notable.length > 0;
                    if (!hasNote && !hasNotable) {
                      return <p className="text-sm text-silver-mute italic">No highlights captured.</p>;
                    }
                    return (
                      <div className="space-y-3">
                        {hasNote && (
                          <div>
                            {h.completed_at && (
                              <p className="text-xs text-silver-mute mb-1">Completed {formatDate(h.completed_at)}</p>
                            )}
                            <p className="text-sm text-silver-text">{h.completion_note}</p>
                          </div>
                        )}
                        {hasNotable && (
                          <ul className="space-y-2 mt-2">
                            {h.notable.map((n, i) => (
                              <li key={i} className="text-sm border-l-2 border-gray-200 pl-3">
                                <span className="font-bold text-silver-text">{n.topic}: </span>
                                <span className="text-silver-mute">{n.detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Admin notes thread — read-only display + prepend form (no autosave PATCH) */}
                <div>
                  <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">
                    Admin Notes <span className="text-[0.6rem] font-normal normal-case tracking-normal">(internal only)</span>
                  </h3>
                  {recruit?.admin_notes ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto text-xs font-mono text-silver-text whitespace-pre-wrap break-words mb-3">
                      {recruit.admin_notes}
                    </div>
                  ) : (
                    <p className="text-sm text-silver-mute italic mb-3">No notes yet.</p>
                  )}
                  <div className="space-y-2">
                    <textarea
                      value={noteInput}
                      onChange={e => setNoteInput(e.target.value)}
                      placeholder="Add a note…"
                      rows={2}
                      className="input resize-none text-sm w-full"
                    />
                    <button
                      type="button"
                      onClick={handleAddNote}
                      disabled={noteSubmitting || !noteInput.trim()}
                      className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {noteSubmitting ? 'Saving…' : 'Add Note'}
                    </button>
                  </div>
                </div>

                {/* Chat transcript */}
                <JsonViewer data={recruit.transcript} title="Chat Transcript" />

              </div>
            )}

            {/* ----------------------------------------------------------------
                Payments (Build B §6.6) — the profile's first money panel.
                Covers all three payment systems; arrears blocks carry a
                staff-only Request-payment control. Read-only otherwise (D19).
            ---------------------------------------------------------------- */}
            {activeTab === 'payments' && <PaymentsTab recruitId={id} me={me} />}
            {activeTab === 'outreach' && <OutreachTab recruitId={id} role={me?.team?.role} />}
            {activeTab === 'callsheet' && <CallSheetTab recruitId={id} me={me} />}
            {activeTab === 'plans' && <PlansTab recruitId={id} />}

          </div>
        </div>
      </main>
    </div>
  );
}

// =============================================================================
// PaymentsTab (Build B §6.6) — self-contained: fetches /money on mount, renders
// one block per enrollment (full / plan / arrears), refunded payments badged,
// recruit-level deliverables beneath. Arrears blocks expose the staff-only
// Request-payment control (→ POST /api/invoices/:enrollmentId/ask).
// =============================================================================
// ─── S100 Build 2 — invite the player to their own profile ───────────────────
// POST /api/recruits/:id/invite-player. The server enforces everything (email
// on file, staff-email collision, re-invite = recovery link); this control
// only reports what the server said, verbatim.
function InvitePlayerButton({ recruitId, linked }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);   // { text, tone: 'ok' | 'err' }

  async function onInvite() {
    setBusy(true);
    setNote(null);
    try {
      const body = await api.post(`/api/recruits/${recruitId}/invite-player`, {});
      setNote({
        text: body.invite_sent
          ? `Invite sent to ${body.sent_to}.`
          : `Account linked, but the email failed: ${body.invite_error}. Re-send to retry.`,
        tone: body.invite_sent ? 'ok' : 'err',
      });
    } catch (err) {
      setNote({ text: err.body?.error || err.message, tone: 'err' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {note && (
        <span className={`text-xs ${note.tone === 'ok' ? 'text-mid-green' : 'text-red-700'}`}>{note.text}</span>
      )}
      <button
        type="button"
        onClick={onInvite}
        disabled={busy}
        className="text-xs font-bold px-3 py-1.5 rounded border border-gray-300 text-hunter hover:border-mid-green disabled:opacity-50"
        title={linked ? 'Player login exists — sends a fresh sign-in link' : 'Email the player a link to set up their own profile login'}
      >
        {busy ? 'Sending…' : linked ? 'Re-send player invite' : 'Invite player'}
      </button>
    </div>
  );
}

// ─── S100 Build 3 — publish the public profile page ──────────────────────────
// The server mints the slug (first publish only, frozen forever) and owns the
// published state; this control reports and copies. Unpublish keeps the slug
// so a re-publish returns the same URL.
function PublishProfileButton({ recruitId, slug, publishedAt, onChange }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [copied, setCopied] = useState(false);
  const published = !!publishedAt;
  const url = slug ? `https://player.ccfrecruiting.com/${slug}` : null;

  async function toggle() {
    setBusy(true);
    setNote(null);
    try {
      const body = await api.post(`/api/recruits/${recruitId}/profile/${published ? 'unpublish' : 'publish'}`, {});
      onChange({ slug: body.slug, published_at: body.published_at });
    } catch (err) {
      setNote(err.body?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard can be refused; the URL is visible in the title attr */ }
  }

  return (
    <div className="flex items-center gap-2">
      {note && <span className="text-xs text-red-700">{note}</span>}
      {published && url && (
        <button
          type="button"
          onClick={copy}
          title={url}
          className="text-xs font-bold px-3 py-1.5 rounded border border-gray-300 text-hunter hover:border-mid-green"
        >
          {copied ? 'Copied' : 'Copy public URL'}
        </button>
      )}
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`text-xs font-bold px-3 py-1.5 rounded ${published
          ? 'border border-gray-300 text-silver-mute hover:border-gray-400'
          : 'bg-mid-green text-white hover:opacity-90'} disabled:opacity-50`}
        title={published ? `Live at ${url}` : 'Make this player’s public profile page live'}
      >
        {busy ? 'Working…' : published ? 'Unpublish page' : 'Publish page'}
      </button>
    </div>
  );
}

function PaymentsTab({ recruitId, me }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.get(`/api/recruits/${recruitId}/money`));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [recruitId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-silver-mute">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const enrollments = data?.enrollments || [];
  const deliverables = data?.deliverables || [];
  if (enrollments.length === 0 && deliverables.length === 0) {
    return <p className="text-sm text-silver-mute italic">No payment history for this recruit.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">Enrollments</h3>
        {enrollments.length === 0 ? (
          <p className="text-sm text-silver-mute italic">No enrollments yet.</p>
        ) : (
          <div className="space-y-4">
            {enrollments.map(e => (
              <EnrollmentBlock key={e.id} enrollment={e} onChanged={load} canAsk={can(me, 'invoice.ask')} />
            ))}
          </div>
        )}
      </div>

      {deliverables.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-sm text-silver-mute uppercase tracking-wider mb-3">Deliverables</h3>
          <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
            {deliverables.map(d => (
              <div key={d.id} className="px-4 py-2 text-sm flex items-baseline justify-between gap-3">
                <span className="text-silver-text">
                  {d.service_name}{d.quantity_purchased ? ` ×${d.quantity_purchased}` : ''}
                </span>
                <span className="text-xs text-silver-mute whitespace-nowrap">
                  {d.delivered_count}/{d.quantity_purchased ?? '—'} · {d.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EnrollmentBlock({ enrollment: e, onChanged, canAsk }) {
  const succeeded = (e.payments || []).filter(p => p.status === 'succeeded');
  // Net-of-refunds, matching the server balance helper: succeeded pay full,
  // partial refunds pay net, full refunds count 0.
  const paidSum = (e.payments || [])
    .filter(p => p.status === 'succeeded' || p.status === 'partially_refunded')
    .reduce((s, p) => s + Math.max(0, Number(p.amount) - Number(p.amount_refunded || 0)), 0);
  const lastPay = succeeded.length ? succeeded[succeeded.length - 1] : null;
  const owed = Number(e.total_owed || 0);
  // A10: render the server-computed balance (/money owns the net-of-refunds
  // math via the one shared helper). Fall back to the client derivation only if
  // the server didn't send one (non-arrears rows carry balance === null).
  const balance = e.balance != null
    ? Number(e.balance)
    : Math.round((owed - paidSum) * 100) / 100;

  let header;
  if (e.kind === 'arrears') {
    header = (
      <span className="font-bold text-hunter">
        {balance > 0
          ? `Owing $${balance.toLocaleString()} of $${owed.toLocaleString()}`
          : `Paid in full · $${owed.toLocaleString()}`}
      </span>
    );
  } else if (e.kind === 'plan') {
    header = (
      <span className="font-bold text-hunter">
        Plan · {e.plan_installments_paid ?? 0} paid
        {e.plan_installment_amount ? ` · $${Number(e.plan_installment_amount).toLocaleString()}/mo` : ''}
      </span>
    );
  } else {
    header = <span className="font-bold text-hunter">Paid · ${paidSum.toLocaleString()}</span>;
  }

  const meta = e.kind === 'arrears'
    ? (lastPay ? `last payment ${formatDate(lastPay.created_at)}` : 'no payments yet')
    : e.kind === 'plan'
      ? `${e.plan_status || 'active'}${e.plan_next_charge_date ? ` · next ${formatDate(e.plan_next_charge_date)}` : ''}`
      : (lastPay ? formatDate(lastPay.created_at) : '');

  return (
    <div className="border border-gray-200 rounded-md p-4 space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        {header}
        <span className="text-xs text-silver-mute whitespace-nowrap">{meta}</span>
      </div>

      {(e.payments || []).length > 0 && (
        <ul className="text-xs divide-y divide-gray-100 border-t border-gray-100">
          {e.payments.map(p => {
            const refunded = p.status === 'refunded' || p.status === 'partially_refunded';
            return (
              <li key={p.id} className="py-1.5 flex items-center justify-between gap-2">
                <span className={refunded ? 'line-through text-silver-mute' : 'text-silver-text'}>
                  ${Number(p.amount).toLocaleString()} · {formatDate(p.created_at)}
                </span>
                <span className={`text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  refunded ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {p.status === 'partially_refunded' ? 'partial refund' : p.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {canAsk && e.kind === 'arrears' && balance > 0 && (
        <AskControl enrollmentId={e.id} remaining={balance} onSent={onChanged} />
      )}
    </div>
  );
}

function AskControl({ enrollmentId, remaining, onSent }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  async function send() {
    setErr(null);
    setMsg(null);
    const n = parseInt(String(amount).replace(/[^0-9]/g, ''), 10);
    if (!Number.isInteger(n) || n < 1) { setErr('Enter a whole dollar amount of at least $1.'); return; }
    if (n > remaining) { setErr(`Cannot exceed the remaining balance of $${remaining.toLocaleString()}.`); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/api/invoices/${enrollmentId}/ask`, { amount: n });
      setMsg(res.email_sent ? `Payment request for $${n.toLocaleString()} sent.` : `Saved, but email failed: ${res.email_error}`);
      setAmount('');
      if (onSent) onSent();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pt-2 border-t border-gray-100">
      <div className="flex items-center gap-2">
        <span className="text-xs text-silver-mute">Request payment $</span>
        <input
          value={amount}
          onChange={e => setAmount(e.target.value)}
          inputMode="numeric"
          placeholder={String(remaining)}
          className="input text-sm w-24 py-1"
        />
        <button
          type="button"
          onClick={send}
          disabled={submitting}
          className="btn-primary text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Sending…' : 'Send'}
        </button>
      </div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
      {msg && <p className="text-xs text-mid-green mt-1">{msg}</p>}
    </div>
  );
}
